'use client'

import * as React from 'react'

import { Check, ChevronsUpDown, LoaderCircleIcon, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { parseAsString, useQueryState } from 'nuqs'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'

import { cn } from '@/utils'

export interface MultiSelectProps {
  'aria-label'?: string
  asyncSearch?: boolean
  className?: string
  defaultValue?: string[]
  disabled?: boolean
  isLoading?: boolean
  maxCount?: number
  onValueChange: (values: string[]) => void
  options: Option[]
  placeholder?: string
  searchable?: boolean
  searchParam?: string
  variant?: 'default' | 'inverted' | 'secondary'
}

export interface MultiSelectRef {
  clear: () => void
  focus: () => void
  reset: () => void
  setSelectedValues: (values: string[]) => void
}

export interface Option {
  icon?: React.ComponentType<{ className?: string }>
  label: string
  style?: {
    badgeColor?: string
    gradient?: string
  }
  value: string
}

const MultiSelect = React.forwardRef<MultiSelectRef, MultiSelectProps>(
  (
    {
      'aria-label': ariaLabel,
      asyncSearch = false,
      className,
      defaultValue = [],
      disabled = false,
      isLoading = false,
      maxCount,
      onValueChange,
      options,
      placeholder,
      searchable = true,
      searchParam = 'search-options',
      variant = 'default'
    },
    ref
  ) => {
    const t = useTranslations('components.form.multiSelect')
    const defaultPlaceholder = placeholder ?? t('placeholder')
    const [open, setOpen] = React.useState(false)
    const [selectedValues, setSelectedValues] =
      React.useState<string[]>(defaultValue)
    const [search, setSearch] = useQueryState(
      searchParam,
      parseAsString.withDefault('')
    )

    const inputRef = React.useRef<HTMLButtonElement>(null)

    // Sync internal state with defaultValue prop changes
    React.useEffect(() => {
      setSelectedValues(defaultValue)
    }, [defaultValue])

    React.useImperativeHandle(ref, () => ({
      clear: () => {
        setSelectedValues([])
        onValueChange([])
      },
      focus: () => {
        inputRef.current?.focus()
      },
      reset: () => {
        setSelectedValues([])
        onValueChange([])
      },
      setSelectedValues: (values: string[]) => {
        setSelectedValues(values)
        onValueChange(values)
      }
    }))

    const handleSelect = (value: string) => {
      const newSelectedValues = selectedValues.includes(value)
        ? selectedValues.filter((item) => item !== value)
        : [...selectedValues, value]

      setSelectedValues(newSelectedValues)
      onValueChange(newSelectedValues)
    }

    const handleRemove = (value: string) => {
      const newSelectedValues = selectedValues.filter((item) => item !== value)
      setSelectedValues(newSelectedValues)
      onValueChange(newSelectedValues)
    }

    const getVariantClasses = () => {
      switch (variant) {
        case 'inverted':
          return 'bg-background text-foreground'
        case 'secondary':
          return 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
        default:
          return 'bg-background text-foreground hover:bg-danger/80'
      }
    }

    const getSelectedOptions = () => {
      return options.filter((option) => selectedValues.includes(option.value))
    }

    const displayValues = getSelectedOptions()
    const displayCount = maxCount
      ? Math.min(displayValues.length, maxCount)
      : displayValues.length
    const hasMore = maxCount && displayValues.length > maxCount

    return (
      <div className={cn('w-full', className)}>
        <Popover onOpenChange={setOpen} open={open}>
          <PopoverTrigger asChild>
            <Button
              aria-expanded={open}
              aria-label={ariaLabel}
              className={cn(
                'h-auto min-h-[42px] w-full justify-between',
                getVariantClasses()
              )}
              disabled={disabled}
              ref={inputRef}
              role="combobox"
              variant="outline"
              wrapChildren={false}
            >
              <div className="flex flex-1 flex-wrap items-center gap-1 py-1">
                {displayValues.slice(0, displayCount).map((option) => (
                  <Badge
                    key={option.value}
                    style={{
                      background: option.style?.gradient
                        ? `linear-gradient(135deg, ${option.style.gradient})`
                        : undefined,
                      backgroundColor: option.style?.badgeColor
                    }}
                    variant="secondary"
                  >
                    {option.icon && <option.icon className="me-1 h-3 w-3" />}
                    {option.label}
                    <span
                      aria-label={`${t('removeLabel')} ${option.label}`}
                      className="ring-offset-background focus:ring-ring ms-1 inline-flex cursor-pointer items-center justify-center rounded-full outline-none focus:ring-2 focus:ring-offset-2"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleRemove(option.value)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          e.stopPropagation()
                          handleRemove(option.value)
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <X className="text-muted-foreground hover:text-foreground h-3 w-3" />
                    </span>
                  </Badge>
                ))}
                {hasMore && (
                  <Badge className="me-1 mb-1" variant="outline">
                    +{displayValues.length - displayCount} {t('more')}
                  </Badge>
                )}
                {selectedValues.length === 0 && (
                  <span className="text-muted-foreground">
                    {defaultPlaceholder}
                  </span>
                )}
              </div>
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-full p-0">
            <Command>
              {searchable && (
                <CommandInput
                  onValueChange={asyncSearch ? setSearch : undefined}
                  placeholder={t('searchPlaceholder')}
                  value={asyncSearch ? search : undefined}
                />
              )}
              <CommandList>
                {isLoading && (
                  <div className="flex justify-center gap-1 py-6 text-sm">
                    <LoaderCircleIcon className="animate-spin" />
                    {t('loading')}
                  </div>
                )}
                {!isLoading && (
                  <CommandEmpty>{t('noOptionsFound')}</CommandEmpty>
                )}
                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      className="cursor-pointer"
                      key={option.value}
                      keywords={[option.value]}
                      onSelect={() => handleSelect(option.value)}
                      value={option.label}
                    >
                      <Check
                        className={cn(
                          'me-2 h-4 w-4',
                          selectedValues.includes(option.value)
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                      {option.icon && <option.icon className="me-2 h-4 w-4" />}
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    )
  }
)

MultiSelect.displayName = 'MultiSelect'

export { MultiSelect }
